import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import Constants from "../../../../Constants";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../../MainViewContainer";
import IdCopyView from "../../../../Components/ToothFairyComponents/IdCopyView";
import {
  BuilderSection,
  PassthroughSection,
  useBuilderDirtyTracking,
} from "./BuilderSections";

const CONNECTION_SECTION_FIELD_MAP = {
  profile: ["name", "description"],
  credentials: ["type", "host", "port", "username", "password"],
};

const ConnectionSettingsAIView = ({
  isEdit,
  settingType,
  handleDeletion,
  onUpdateSecret,
  isUpdatingSecret,
  isBuilderLayout,
  builderActiveSection,
  onBuilderContextChange,
  saveVersion,
}) => {
  const { t } = useTranslation();
  const { currentTheme, mode } = AppTheme.useTheme();
  const entityName = t("settings.connection.entityName");
  const {
    setFieldValue,
    values,
    validationSchema,
    initialValues,
    errors,
    submitCount,
  } = AppForms.useFormikContext();
  const { state } = AppStateManager.useAppStateManager();
  const { getWorkspaceBaseModel } = useWorkspaces();
  const _title = useCallback(() => {
    return isEdit ? t("common.save") : t("common.create");
  }, [entityName, isEdit, t]);

  const _showBuilderSection = (sectionId) =>
    builderActiveSection == null || builderActiveSection === sectionId;
  const SectionRenderer = isBuilderLayout ? BuilderSection : PassthroughSection;

  useBuilderDirtyTracking({
    isBuilderLayout,
    values,
    initialValues,
    saveVersion,
    onBuilderContextChange,
    errors,
    submitCount,
    sectionFieldMap: CONNECTION_SECTION_FIELD_MAP,
  });

  const handleUpdateSecretClick = useCallback(() => {
    setFieldValue("password", "");
    onUpdateSecret();
  }, [setFieldValue, onUpdateSecret]);

  return (
    <>
      {_showBuilderSection("profile") && (
        <SectionRenderer
          title={t("connections.builderSectionProfile", {
            defaultValue: "Overview",
          })}
          icon={AppAssets.icons.persona}
        >
          {isEdit && (
            <IdCopyView id={values?.id} label={t("settings.connection.id")} />
          )}
          <AppForms.AppFormField
            showTextInputLabel
            isMandatory
            wrapperInputStyle={{
              paddingTop: 10,
            }}
            label={t("settings.connection.databaseName")}
            placeholder={t("settings.connection.databaseNamePlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="name"
          />
          <AppForms.AppFormField
            showTextInputLabel
            wrapperInputStyle={{
              paddingTop: 10,
            }}
            multiline
            textContainer={{
              minHeight: 120,
              paddingVertical: 10,
              marginHorizontal: 5,
            }}
            label={t("settings.connection.description")}
            placeholder={t("settings.connection.descriptionPlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="description"
          />
        </SectionRenderer>
      )}
      {_showBuilderSection("credentials") && (
        <SectionRenderer
          title={t("connections.builderSectionCredentials", {
            defaultValue: "Credentials",
          })}
          icon={AppAssets.icons.credentials}
        >
          <AppForms.AppFormDropDown
            isElevated={false}
            dropDownStyle={{
              borderColor: currentTheme?.theme?.border_color,
              maxWidth: !isLargeScreen() && 250,
            }}
            inputLabelStyle={{
              marginLeft: 5,
            }}
            showTextInputLabel
            label={t("settings.connection.dbType")}
            isMandatory
            name="type"
            items={[
              {
                id: "sql",
                name: "Microsoft SQL Server",
              },
              {
                id: "oracle",
                name: "Oracle",
              },
              {
                id: "mysql",
                name: "MySQL",
              },
              {
                id: "postgressql",
                name: "PostgresSQL",
              },
            ]}
            propertyLabel="name"
            propertyValue="id"
          />
          <AppForms.AppFormField
            showTextInputLabel
            isMandatory
            wrapperInputStyle={{
              paddingTop: 10,
            }}
            label={t("settings.connection.host")}
            placeholder={t("settings.connection.hostPlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="host"
          />
          <AppForms.AppFormField
            showTextInputLabel
            isMandatory
            wrapperInputStyle={{
              paddingTop: 10,
            }}
            label={t("settings.connection.port")}
            placeholder={t("settings.connection.portPlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="port"
          />
          <AppForms.AppFormField
            showTextInputLabel
            wrapperInputStyle={{
              paddingTop: 10,
            }}
            label={t("settings.connection.username")}
            placeholder={t("settings.connection.usernamePlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="username"
          />
          <View style={{ position: "relative" }}>
            <AppForms.AppFormField
              showTextInputLabel
              wrapperInputStyle={{
                paddingTop: 10,
              }}
              label={t("settings.connection.password")}
              placeholder={
                isEdit && !isUpdatingSecret
                  ? t("settings.authorisation.passwordCannotBeViewed")
                  : isEdit && isUpdatingSecret
                  ? t("settings.authorisation.enterNewPassword")
                  : t("settings.authorisation.passwordPlaceholder")
              }
              keyboardtype="default"
              textContextType="label"
              autoCorrect={false}
              name="password"
              disabled={isEdit && !isUpdatingSecret}
            />
            {isEdit && !isUpdatingSecret && (
              <ToothFairyButton
                title={t("settings.authorisation.updatePassword")}
                onPress={handleUpdateSecretClick}
                style={{
                  height: 25,
                  paddingHorizontal: 0,
                }}
                btnContentStyle={{
                  paddingHorizontal: 5,
                }}
                backgroundColor={currentTheme?.theme?.soft_primary}
                wrapperStyle={{
                  position: "absolute",
                  right: 0,
                  top: 49,
                  minWidth: 140,
                  height: 25,
                  zIndex: 10,
                }}
              />
            )}
          </View>
        </SectionRenderer>
      )}
      {!isBuilderLayout && (
        <View style={{ alignItems: "center" }}>
          <AppForms.AppSubmitButton
            title={_title()}
            awesomeIconColor={currentTheme?.theme?.white}
            backgroundColor={currentTheme?.theme?.primary}
            btnContentStyle={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
            style={[
              {
                height: Constants.appButtonHeight,
                width: "auto",
                shadowColor: currentTheme?.theme?.transparent,
                marginHorizontal: 10,
              },
            ]}
            // btnLabelStyle={{ fontSize: 16 }}
            wrapperStyle={[
              {
                width: "fit-content",
                minWidth: 150,
                marginTop: 20,
                height: Constants.appButtonHeight,
              },
            ]}
          />
          {isEdit && (
            <ToothFairyButton
              title={t("common.delete")}
              onPress={handleDeletion}
              isDeletion
              wrapperStyle={{
                marginTop: 30,
                minWidth: 150,
                height: Constants.appButtonHeight,
              }}
            />
          )}
        </View>
      )}
    </>
  );
};

export default ConnectionSettingsAIView;
